/**
 * VINCULAR MINHA FEDERAÇÃO (Fluxo D — decisão do usuário)
 *
 * Quem se cadastra como FEDERAÇÃO cai aqui, em vez do painel. Sem criar cadastro
 * novo: ele escolhe uma federação que já existe (pedindo o vínculo) ou cria a
 * dele. A federação nova nasce "pendente" e só passa a valer com o ok do Super
 * Admin — e só depois desse ok ela pode analisar documentos (G-1).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { federationApi, documentosApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, CheckCircle2, Clock, Loader2, Plus, Search, ShieldCheck, Upload, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const FORM_VAZIO = {
  name: '', cnpj: '', city: '', state: '', responsible: '', phone: '', email: '',
};

const ROTULO_PEDIDO = {
  pendente: { texto: 'Em análise', cor: 'text-amber-300' },
  aprovado: { texto: 'Aprovado', cor: 'text-emerald-400' },
  recusado: { texto: 'Recusado', cor: 'text-red-400' },
};

export default function VincularFederacaoPage() {
  const { user } = useAuth();
  const [carregando, setCarregando] = useState(true);
  const [minha, setMinha] = useState(null);
  const [lista, setLista] = useState([]);
  const [busca, setBusca] = useState('');
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [pedindo, setPedindo] = useState('');
  const [rgEnviado, setRgEnviado] = useState({});
  const [anexandoRg, setAnexandoRg] = useState('');
  // Fase 2 (item 8 / opção B): mensagem de erro mostrada junto ao campo.
  const [erros, setErros] = useState({});

  const souSuperAdmin = user?.role === 'super_admin';

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [respostaMinha, respostaLista] = await Promise.all([
        federationApi.mine(),
        federationApi.list(),
      ]);
      setMinha(respostaMinha.data);
      setLista(respostaLista.data || []);
    } catch (erro) {
      toast.error(erro?.response?.data?.detail || 'Erro ao carregar as federações');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // O e-mail e o nome do responsável JÁ são conhecidos (são os do cadastro da
  // conta). Vêm preenchidos — e continuam EDITÁVEIS (decisão B): o líder pode
  // trocar pelo e-mail da secretaria, por exemplo. Só preenche campo vazio,
  // para nunca apagar o que ele digitou.
  // ⚠️ Depende dos VALORES (não do objeto `user`, que troca de identidade a cada
  // render) e sai sem alterar nada quando não há o que preencher — senão o
  // efeito se realimenta em laço infinito.
  const emailDaConta = user?.email || '';
  const nomeDaConta = user?.name || '';
  useEffect(() => {
    if (!emailDaConta && !nomeDaConta) return;
    setForm((atual) => {
      const email = atual.email || emailDaConta;
      const responsible = atual.responsible || nomeDaConta;
      if (email === atual.email && responsible === atual.responsible) return atual;
      return { ...atual, email, responsible };
    });
  }, [emailDaConta, nomeDaConta]);

  const minhaFederacao = minha?.federation || null;
  const pedidoPendente = (minha?.requests || []).find((p) => p.status === 'pendente');

  const filtradas = lista.filter((f) => {
    const texto = busca.trim().toLowerCase();
    if (!texto) return true;
    return String(f.name || '').toLowerCase().includes(texto);
  });

  const pedirVinculo = async (federacao) => {
    setPedindo(federacao._id);
    try {
      await federationApi.requestLink(federacao._id, { document_id: rgEnviado[federacao._id] });
      toast.success(`Pedido enviado para ${federacao.name}`);
      await carregar();
    } catch (erro) {
      toast.error(erro?.response?.data?.detail || 'Erro ao pedir o vínculo');
    } finally {
      setPedindo('');
    }
  };

  // G-2: o pedido de vínculo EXIGE o documento do responsável — ele é anexado
  // aqui mesmo, antes de pedir (burocracia externa fora do sistema).
  const anexarRg = async (federacao, arquivo) => {
    if (!arquivo) return;
    setAnexandoRg(federacao._id);
    try {
      const { data } = await documentosApi.enviar(
        'federacao', federacao._id, 'rg_pretendente', arquivo);
      setRgEnviado((atual) => ({ ...atual, [federacao._id]: data.id }));
      toast.success('Documento anexado. Agora você já pode pedir o vínculo.');
    } catch (erro) {
      toast.error(erro?.response?.data?.detail || 'Erro ao anexar o documento');
    } finally {
      setAnexandoRg('');
    }
  };

  const criarFederacao = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setErros((atual) => ({ ...atual, name: 'Informe o nome da federação' }));
      return;
    }
    setSalvando(true);
    try {
      await federationApi.claim(form);
      toast.success('Federação criada! Aguardando o ok do Super Admin.');
      setForm(FORM_VAZIO);
      setErros({});
      await carregar();
    } catch (erro) {
      toast.error(erro?.response?.data?.detail || 'Erro ao criar a federação');
    } finally {
      setSalvando(false);
    }
  };

  // Fase 2 (item 8 / opção B): o helper agora marca o campo com erro e limpa a
  // mensagem quando o usuário começa a corrigir. Só o campo `name` tem
  // validação, então nos outros nada muda.
  const campo = (nome) => ({
    value: form[nome],
    onChange: (e) => {
      if (erros[nome]) setErros((atual) => ({ ...atual, [nome]: undefined }));
      setForm({ ...form, [nome]: e.target.value });
    },
    className: 'bg-[#1A1A1A] border-[#27272A] text-white',
    'aria-invalid': erros[nome] ? true : undefined,
  });

  return (
    <MainLayout>
      <div className="space-y-6 max-w-4xl" data-testid="vincular-federacao-page">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Chivo']">Vincular minha federação</h1>
          <p className="text-[#A1A1AA] mt-1">
            Escolha a federação que você representa — ou crie a sua, se ela ainda não existe.
          </p>
        </div>

        {carregando ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
          </div>
        ) : souSuperAdmin ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent className="p-6 space-y-3">
              <p className="text-white font-semibold">Você é Super Admin.</p>
              <p className="text-sm text-[#A1A1AA]">
                Os pedidos de vínculo e as federações novas esperando aprovação aparecem na tela
                de Federações, no bloco <strong>Aguardando aprovação</strong>.
              </p>
              <Button asChild className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
                <Link to="/federations">Ir para Federações</Link>
              </Button>
            </CardContent>
          </Card>
        ) : minhaFederacao ? (
          <>
            <Card className="bg-[#141414] border-[#27272A]" data-testid="minha-federacao">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">{minhaFederacao.name}</h2>
                    <p className="text-xs text-[#71717A]">
                      Você é {minha?.role === 'owner' ? 'o responsável' : 'analista'} desta federação
                    </p>
                  </div>
                </div>

                {minhaFederacao.valendo ? (
                  <p className="flex items-center gap-2 text-sm text-emerald-400">
                    <ShieldCheck size={16} /> Verificada — o Super Admin já deu o ok.
                  </p>
                ) : minhaFederacao.status === 'recusada' ? (
                  <div className="space-y-2">
                    <p className="flex items-center gap-2 text-sm text-red-400">
                      <XCircle size={16} /> Recusada pelo Super Admin.
                    </p>
                    {minhaFederacao.rejected_reason && (
                      <p className="text-xs text-red-300" data-testid="motivo-recusa">
                        Motivo: {minhaFederacao.rejected_reason}
                      </p>
                    )}
                    <p className="text-xs text-[#71717A]">
                      Na página de Federações: use <strong>Editar dados</strong> para cumprir a
                      exigência e depois <strong>Corrigir e reenviar</strong> para voltar à análise.
                    </p>
                    <Button asChild size="sm" data-testid="ir-editar-federacao"
                            className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
                      <Link to="/federations">Corrigir dados</Link>
                    </Button>
                  </div>
                ) : (
                  <p className="flex items-center gap-2 text-sm text-amber-300">
                    <Clock size={16} /> Aguardando o ok do Super Admin. Você já pode editar os dados,
                    mas ainda não pode analisar documentos de professores e equipes.
                  </p>
                )}

                <Button asChild variant="outline"
                        className="border-[#27272A] text-[#A1A1AA] hover:text-white">
                  <Link to="/federations">Ver a página de Federações</Link>
                </Button>
              </CardContent>
            </Card>

            {minhaFederacao.valendo && (
              <p className="text-sm text-[#71717A]">
                A partir do ok do Super Admin, você passa a poder analisar documentos de
                professores e equipes vinculados a esta federação (o envio de documentos entra na
                próxima etapa).
              </p>
            )}
          </>
        ) : (
          <>
            {pedidoPendente && (
              <Card className="bg-amber-500/5 border-amber-500/30">
                <CardContent className="p-4 flex items-center gap-3">
                  <Clock className="w-5 h-5 text-amber-300" />
                  <p className="text-sm text-amber-200">
                    Seu pedido de vínculo para <strong>{pedidoPendente.federation_name}</strong> está
                    em análise com o Super Admin.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* A) A federação já existe: pedir vínculo */}
            <Card className="bg-[#141414] border-[#27272A]">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h2 className="text-lg font-bold text-white">A federação já existe?</h2>
                  <p className="text-sm text-[#A1A1AA]">
                    Peça o vínculo. Quem decide é o Super Admin (e o responsável atual, se houver).
                    É preciso anexar o <strong>RG do responsável</strong> — exigência da parte de fora.
                  </p>
                </div>

                <div className="relative max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={16} />
                  <Input
                    placeholder="Buscar federação..."
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    data-testid="buscar-federacao"
                    className="pl-10 bg-[#1A1A1A] border-[#27272A] text-white"
                  />
                </div>

                {filtradas.length === 0 ? (
                  <p className="text-sm text-[#71717A]" data-testid="sem-federacoes">
                    Nenhuma federação encontrada. Se a sua ainda não existe, crie abaixo.
                  </p>
                ) : (
                  <ul className="divide-y divide-[#27272A]" data-testid="lista-federacoes">
                    {filtradas.map((f) => (
                      <li key={f._id} className="flex items-center justify-between py-3">
                        <div>
                          <p className="text-white">{f.name}</p>
                          {f.city && (
                            <p className="text-xs text-[#71717A]">
                              {f.city}{f.state ? ` - ${f.state}` : ''}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <label
                            data-testid={`anexar-rg-${f._id}`}
                            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-bold cursor-pointer ${
                              rgEnviado[f._id]
                                ? 'border-emerald-500/50 text-emerald-400'
                                : 'border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10'}`}
                          >
                            {anexandoRg === f._id
                              ? <Loader2 size={16} className="animate-spin" />
                              : rgEnviado[f._id] ? <CheckCircle2 size={16} /> : <Upload size={16} />}
                            {rgEnviado[f._id] ? 'RG anexado' : 'Anexar RG'}
                            <Input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              className="hidden"
                              data-testid={`arquivo-rg-${f._id}`}
                              onChange={(e) => anexarRg(f, e.target.files?.[0])}
                            />
                          </label>
                          <Button
                            size="sm"
                            onClick={() => pedirVinculo(f)}
                            disabled={!rgEnviado[f._id] || Boolean(pedidoPendente) || pedindo === f._id}
                            data-testid={`pedir-vinculo-${f._id}`}
                            className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold disabled:opacity-50"
                          >
                            {pedindo === f._id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : 'Solicitar vínculo'}
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* B) Não existe: criar a federação (dono provisório) */}
            <Card className="bg-[#141414] border-[#27272A]">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h2 className="text-lg font-bold text-white">Ainda não existe? Crie a sua</h2>
                  <p className="text-sm text-[#A1A1AA]">
                    Você fica como responsável provisório e a federação só passa a valer depois do
                    ok do Super Admin.
                  </p>
                </div>

                <form onSubmit={criarFederacao} className="space-y-4" data-testid="form-nova-federacao">
                  <div className="space-y-2">
                    <Label htmlFor="nova-fed-nome" className="text-[#A1A1AA]">Nome *</Label>
                    <Input {...campo('name')} id="nova-fed-nome" required placeholder="Ex.: FJJ-RIO"
                           data-testid="nova-federacao-nome" />
                    {erros.name && (
                      <p role="alert" className="text-xs text-red-400 mt-1">{erros.name}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nova-fed-cnpj" className="text-[#A1A1AA]">CNPJ</Label>
                      <Input {...campo('cnpj')} id="nova-fed-cnpj" placeholder="00.000.000/0000-00"
                             data-testid="nova-federacao-cnpj" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nova-fed-responsavel" className="text-[#A1A1AA]">Responsável</Label>
                      <Input {...campo('responsible')} id="nova-fed-responsavel" placeholder="Nome do responsável"
                             data-testid="nova-federacao-responsavel" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nova-fed-cidade" className="text-[#A1A1AA]">Cidade</Label>
                      <Input {...campo('city')} id="nova-fed-cidade" data-testid="nova-federacao-cidade" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nova-fed-uf" className="text-[#A1A1AA]">UF</Label>
                      <Input {...campo('state')} id="nova-fed-uf" maxLength={2} data-testid="nova-federacao-uf" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nova-fed-telefone" className="text-[#A1A1AA]">Telefone</Label>
                      <Input {...campo('phone')} id="nova-fed-telefone" data-testid="nova-federacao-telefone" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nova-fed-email" className="text-[#A1A1AA]">E-mail</Label>
                      <Input {...campo('email')} id="nova-fed-email" type="email" data-testid="nova-federacao-email" />
                    </div>
                  </div>
                  <Button type="submit" disabled={salvando} data-testid="criar-federacao"
                          className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
                    {salvando
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <><Plus size={16} className="mr-1" /> Criar federação</>}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {(minha?.requests || []).length > 0 && (
              <Card className="bg-[#141414] border-[#27272A]">
                <CardContent className="p-6 space-y-3">
                  <h2 className="text-lg font-bold text-white">Meus pedidos</h2>
                  <ul className="divide-y divide-[#27272A]" data-testid="meus-pedidos">
                    {(minha.requests || []).map((p) => {
                      const rotulo = ROTULO_PEDIDO[p.status] || ROTULO_PEDIDO.pendente;
                      return (
                        <li key={p._id} className="py-3">
                          <div className="flex items-center justify-between">
                            <span className="text-white">{p.federation_name}</span>
                            <span className={`text-sm font-semibold ${rotulo.cor}`}>
                              {rotulo.texto}
                            </span>
                          </div>
                          {p.reason && (
                            <p className="text-xs text-red-300 mt-1 flex items-center gap-1">
                              <XCircle size={12} /> {p.reason}
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
}
